import { useMemo } from 'react'

import {
  type SanityFilterGroup,
  type SanitySortOptionType,
} from '@data/sanity/queries/types/cart'
import { type SanityProductFragment } from '@data/sanity/queries/types/product'
import {
  compareNumbers,
  compareStrings,
  getAllCombinations,
} from '@lib/helpers'
import { UrlParameter } from '@lib/hooks'
import { type Filter } from '@lib/product'

/**
 * Returns a method that compares products for sorting by price.
 */
const sortProductsByPrice = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { price: price1 }: SanityProductFragment,
    { price: price2 }: SanityProductFragment,
  ) => sign * compareNumbers(price1, price2)
}

/**
 * Returns a method that compares products for sorting by title.
 */
const sortProductsByTitle = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { title: title1 }: SanityProductFragment,
    { title: title2 }: SanityProductFragment,
  ) => sign * compareStrings(title1, title2)
}

/**
 * Returns a method that compares products for sorting by creation date.
 */
const sortProductsByDate = (isDescending = false) => {
  const sign = isDescending ? -1 : 1

  return (
    { _createdAt: createdAt1 }: SanityProductFragment,
    { _createdAt: createdAt2 }: SanityProductFragment,
  ) => sign * compareStrings(createdAt1, createdAt2)
}

/**
 * Collection filter and sort hook.
 */
export const useFilterAndSort = (
  products: SanityProductFragment[],
  featuredProductIds: number[],
  filters: Filter[],
  sortValue: SanitySortOptionType | null,
) => {
  const filteredProducts = useMemo(() => {
    // Get all combinations of all filter values
    const filterCombinations = getAllCombinations(
      ...filters
        .filter((filter) => filter.values.length > 0)
        .map((filter) => filter.values),
    )

    return products.filter((product) => {
      const productFilterValues =
        product.filters?.map((filter) => filter.slug.current) ?? []

      return filterCombinations.some((values) =>
        values.every((value) => productFilterValues.includes(value)),
      )
    })
  }, [filters, products])

  switch (sortValue) {
    case 'priceAsc': {
      return filteredProducts.sort(sortProductsByPrice())
    }

    case 'priceDesc': {
      return filteredProducts.sort(sortProductsByPrice(true))
    }

    case 'alphaAsc': {
      return filteredProducts.sort(sortProductsByTitle())
    }

    case 'alphaDesc': {
      return filteredProducts.sort(sortProductsByTitle(true))
    }

    case 'dateAsc': {
      return filteredProducts.sort(sortProductsByDate())
    }

    case 'dateDesc': {
      return filteredProducts.sort(sortProductsByDate(true))
    }

    case 'featured': {
      const productIndices = featuredProductIds.reduce(
        (result: Record<number, number>, value: number, index: number) => (
          (result[value] = index + 1), result
        ),
        {},
      )

      return filteredProducts.sort(
        (product1: SanityProductFragment, product2: SanityProductFragment) =>
          (productIndices[product1.productID] ?? Infinity) -
          (productIndices[product2.productID] ?? Infinity),
      )
    }

    default: {
      return filteredProducts
    }
  }
}

/**
 * Gets sort parameter value from URL parameters.
 */
export const getSortParameterValue = (activeParameters: UrlParameter[]) => {
  const sortParameter = activeParameters.find(
    (activeParameter) => activeParameter.name === 'sort',
  )
  const value = sortParameter?.value

  if (!value || Array.isArray(value)) {
    return null
  }

  return value as SanitySortOptionType
}

/**
 * Returns a method that gets filter from a URL parameter.
 */
export const getFilterFromUrlParameter =
  (filterGroups: SanityFilterGroup[]) =>
  (activeParameter: UrlParameter): Filter => {
    const filterGroup = filterGroups.find(
      (filterGroup) => filterGroup.slug.current === activeParameter.name,
    )
    const optionSlugs =
      filterGroup?.options?.map((option) => option.slug.current) ?? []
    const values =
      activeParameter.value && Array.isArray(activeParameter.value)
        ? activeParameter.value
        : activeParameter.value?.split(',') ?? []

    return {
      name: activeParameter.name,
      values: [
        ...new Set(
          values.filter((value) => value && optionSlugs.includes(value)),
        ),
      ],
    }
  }

/**
 * Returns a method that gets updated URL parameter from given new parameters.
 */
export const getUpdatedParameter =
  (newParameters: UrlParameter[]) =>
  (parameter: UrlParameter): UrlParameter => {
    const matchedParameter = newParameters.find(
      (newParameter) => newParameter.name === parameter.name,
    )

    if (!matchedParameter) {
      return parameter
    }

    return {
      ...parameter,
      value: matchedParameter?.value ?? null,
    }
  }
